import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { AccessibilityService } from '@app/core/services/accessibility.service';
import { StylingColoursService } from '@app/modules/site-settings-styling/components/styling-colours/services/styling-colours.service';

@Directive({
  selector: '[setColor]'
})
export class SetColorDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input('setColor') color: string;

  @Input('setColorRules') setColorRules: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }[] = [];

  originalStyle: any;
  private isMatSlideToggle: boolean = false;

  // Every inline style this directive wants applied, so accessibility mode can
  // strip brand colours (inline !important beats any stylesheet) and restore them.
  private trackedStyles = new Map<HTMLElement, Map<string, { value: string, priority: string }>>();
  private accessibilityModeSubscription: Subscription;

  constructor(
    private stylingColoursService: StylingColoursService,
    private el: ElementRef,
    private renderer: Renderer2,
    private accessibilityService: AccessibilityService
  ) {
    this.accessibilityModeSubscription = this.accessibilityService.enabled$.subscribe(
      enabled => this.onAccessibilityModeChange(enabled)
    );
  }

  ngOnDestroy(): void {
    this.accessibilityModeSubscription?.unsubscribe();
  }

  private onAccessibilityModeChange(enabled: boolean): void {
    this.trackedStyles.forEach((props, element) => {
      props.forEach(({ value, priority }, prop) => {
        if (enabled) {
          element.style.removeProperty(prop);
        }
        else {
          element.style.setProperty(prop, value, priority);
        }
      });
    });
  }

  private setTrackedStyle(element: HTMLElement, prop: string, value: string, priority: string = ''): void {
    if (!element) return;

    if (value === '' || value === null || value === undefined) {
      this.removeTrackedStyle(element, prop);
      return;
    }

    let props = this.trackedStyles.get(element);
    if (!props) {
      props = new Map();
      this.trackedStyles.set(element, props);
    }
    props.set(prop, { value, priority });

    if (!this.accessibilityService.isEnabled) {
      element.style.setProperty(prop, value, priority);
    }
  }

  private removeTrackedStyle(element: HTMLElement, prop: string): void {
    if (!element) return;

    this.trackedStyles.get(element)?.delete(prop);
    element.style.removeProperty(prop);
  }

  private setTrackedRendererStyle(element: HTMLElement, prop: string, value: string, flags?: RendererStyleFlags2): void {
    const priority = flags !== undefined && (flags & RendererStyleFlags2.Important) ? 'important' : '';
    this.setTrackedStyle(element, prop, value, priority);
  }

  ngOnInit(): void {
    if(this.stylingColoursService.activeStylingColour !== undefined) {
      const hasExplicitColor = !!this.color && this.color !== '';

      if(hasExplicitColor){
        const classes = this.el.nativeElement.classList;

        if(
          classes.contains('mat-stroked-button') ||
          classes.contains('mat-mdc-stroked-button') ||
          classes.contains('mat-mdc-outlined-button') ||
          classes.contains('mat-outline-button')
        ){
          this.setMatStrokedButtonStyling();
        }
        else if(
          classes.contains('mat-raised-button') ||
          classes.contains('mat-mdc-raised-button') ||
          classes.contains('mat-mdc-filled-button') ||
          classes.contains('mat-mdc-button') && classes.contains('mat-primary')
        ){
          this.setMatRaisedButtonStyling();
        }
        else if(
          classes.contains('mat-flat-button') ||
          classes.contains('mat-mdc-flat-button') ||
          classes.contains('mat-mdc-unelevated-button') ||
          classes.contains('mat-mdc-button')
        ){
          this.setMatRaisedButtonStyling();
        }
        else if(
          this.el.nativeElement.classList.contains('mat-slide-toggle') ||
          this.el.nativeElement.classList.contains('mat-mdc-slide-toggle')
        ) {
          this.isMatSlideToggle = true;
        }
        else if(
          this.el.nativeElement.classList.contains('mat-progress-bar') ||
          this.el.nativeElement.classList.contains('mat-mdc-progress-bar') ||
          this.el.nativeElement.classList.contains('mdc-linear-progress')
        ) {
          this.setMatProgressBarStyling();
        }
        else if(
          this.el.nativeElement.classList.contains('mat-checkbox') ||
          this.el.nativeElement.classList.contains('mat-mdc-checkbox') ||
          this.el.nativeElement.classList.contains('mdc-checkbox')
        ) {
          this.observeMatCheckboxClass();
        }
        else if(
          this.el.nativeElement.classList.contains('mat-list-option') ||
          this.el.nativeElement.classList.contains('mat-mdc-list-option') ||
          this.el.nativeElement.classList.contains('mdc-list-item')
        ) {
          this.observeMatListOptionClass();
        }
        else if(
          this.el.nativeElement.classList.contains('mat-radio-button') ||
          this.el.nativeElement.classList.contains('mat-mdc-radio-button') ||
          this.el.nativeElement.classList.contains('mdc-radio')
        ) {
          this.observeMatRadioButtonClass();
        }
        else if(this.el.nativeElement.classList.contains('mat-chip')) {
          this.setMatChipStyling();
        }
        else {
          this.setColorRegardlessOfClass();
        }
      }
      else {
        this.setColorRules.forEach(
          colorRule => {
            if(colorRule?.target === "mat-badge") {
              this.setMatBadgeStyling(colorRule);
            }
            else if(colorRule?.target === "kendo-treeview") {
              // this.setMatBadgeStyling(colorRule);
            }
            else {
              if(colorRule?.events?.includes('hover')){
                this.addHoverListeners(colorRule);
              }

              if(colorRule?.setIfClass) {
                this.observeClassRule(colorRule);
              }

              if(!colorRule?.events?.includes('hover') && !colorRule?.setIfClass) {
                this.setColor(colorRule);
              }
            }
          }
        )
      }
    }
  }

  ngAfterViewInit() {
    if(this.stylingColoursService.activeStylingColour !== undefined) {
      if (this.isMatSlideToggle) {
        this.observeMatSlideToggleCheckedClass();
      }

      this.setColorRules.forEach(
        colorRule => {
          if(colorRule?.events?.includes('routerLinkActive')){
            this.observeRouterLinkActiveClass(colorRule);
          }
          else if(colorRule.target === 'kendo-treeview') {
            this.setInitialKendoTreeviewStyling(colorRule)
            this.observeKendoTreeviewClass(colorRule)
          }
        }
      )
    }
  }

  private observeRouterLinkActiveClass(
    colorRule: {
      color?: string
      style?: string
      setIfClass?: string[]
      events?: string[]
      target?: string
    },
  ) {
    const linkElement = this.el.nativeElement;

    const applyRouterLinkState = (target: HTMLElement) => {
      if (this.haveSharedItems(target.classList, colorRule?.setIfClass)) {
        this.setColor(colorRule);
      }
      else {
        this.removeColorRegardlessOfClass(colorRule);
        this.originalStyle = this.el.nativeElement.getAttribute('style');
      }
    };

    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          applyRouterLinkState(mutation.target as HTMLElement);
        }
      }
    });

    observer.observe(linkElement, { attributes: true });
  }

  haveSharedItems(array1: any[] | DOMTokenList, array2: any[]): boolean {
    const list1 = Array.from(array1 instanceof DOMTokenList ? array1 : array1);
    const set2 = new Set(array2);
    for (const item of list1) {
        if (set2.has(item)) {
            return true;
        }
    }
    return false;
  }

  /*
  .##.....##..#######..##.....##.########.########......######..########.##.......##....##.####.##....##..######.......######..########....###....########..########
  .##.....##.##.....##.##.....##.##.......##.....##....##....##....##....##........##..##...##..###...##.##....##.....##....##....##......##.##...##.....##....##...
  .##.....##.##.....##.##.....##.##.......##.....##....##..........##....##.........####....##..####..##.##...........##..........##.....##...##..##.....##....##...
  .#########.##.....##.##.....##.######...########......######.....##....##..........##.....##..##.##.##.##...####.....######.....##....##.....##.########.....##...
  .##.....##.##.....##..##...##..##.......##...##............##....##....##..........##.....##..##..####.##....##...........##....##....#########.##...##......##...
  .##.....##.##.....##...##.##...##.......##....##.....##....##....##....##..........##.....##..##...###.##....##.....##....##....##....##.....##.##....##.....##...
  .##.....##..#######.....###....########.##.....##.....######.....##....########....##....####.##....##..######.......######.....##....##.....##.##.....##....##...
  */

  private observeClassRule(colorRule: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    const element = this.el.nativeElement;

    const applyRule = () => {
      if (this.haveSharedItems(element.classList, colorRule.setIfClass || [])) {
        this.setColor(colorRule);
      }
      else {
        this.removeColorRegardlessOfClass(colorRule);
      }
    };

    applyRule();

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          applyRule();
        }
      }
    });

    observer.observe(element, { attributes: true, attributeFilter: ['class'] });
  }

  private addHoverListeners(colorRule: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    this.renderer.listen(this.el.nativeElement, 'mouseenter', () => {
      this.setHoverStyle(colorRule);
      // this.setHoverStyle();
    });

    this.renderer.listen(this.el.nativeElement, 'mouseleave', () => {
      this.removeHoverStyle(colorRule);
      // this.removeHoverStyle();
    });
  }

  private setHoverStyle(colorRule?: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    this.setColorRegardlessOfClass(colorRule);
    this.setTrackedRendererStyle(this.el.nativeElement, 'cursor', 'pointer');
  }

  private removeHoverStyle(colorRule?: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    const shouldKeepActiveState = !!colorRule?.setIfClass
      ? this.haveSharedItems(this.el.nativeElement.classList, colorRule.setIfClass)
      : this.el.nativeElement.classList.contains('primaryColor');

    if (shouldKeepActiveState) {
      this.setColor(colorRule);
    }
    else {
      this.removeColorRegardlessOfClass(colorRule);
    }

    this.renderer.removeStyle(this.el.nativeElement, 'cursor');
  }

  /*
  .##.....##..#######..##.....##.########.########......######..########.##....##.##.......####.##....##..######......########.##....##.########.
  .##.....##.##.....##.##.....##.##.......##.....##....##....##....##.....##..##..##........##..###...##.##....##.....##.......###...##.##.....##
  .##.....##.##.....##.##.....##.##.......##.....##....##..........##......####...##........##..####..##.##...........##.......####..##.##.....##
  .#########.##.....##.##.....##.######...########......######.....##.......##....##........##..##.##.##.##...####....######...##.##.##.##.....##
  .##.....##.##.....##..##...##..##.......##...##............##....##.......##....##........##..##..####.##....##.....##.......##..####.##.....##
  .##.....##.##.....##...##.##...##.......##....##.....##....##....##.......##....##........##..##...###.##....##.....##.......##...###.##.....##
  .##.....##..#######.....###....########.##.....##.....######.....##.......##....########.####.##....##..######......########.##....##.########.
  */

  /*
  .##.....##....###....########.....######..##.......####.########..########....########..#######...######....######...##.......########.....######..########.##....##.##.......####.##....##..######.......######..########....###....########..########
  .###...###...##.##......##.......##....##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##..........##....##....##.....##..##..##........##..###...##.##....##.....##....##....##......##.##...##.....##....##...
  .####.####..##...##.....##.......##.......##........##..##.....##.##.............##....##.....##.##........##........##.......##..........##..........##......####...##........##..####..##.##...........##..........##.....##...##..##.....##....##...
  .##.###.##.##.....##....##........######..##........##..##.....##.######.........##....##.....##.##...####.##...####.##.......######.......######.....##.......##....##........##..##.##.##.##...####.....######.....##....##.....##.########.....##...
  .##.....##.#########....##.............##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##................##....##.......##....##........##..##..####.##....##...........##....##....#########.##...##......##...
  .##.....##.##.....##....##.......##....##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##..........##....##....##.......##....##........##..##...###.##....##.....##....##....##....##.....##.##....##.....##...
  .##.....##.##.....##....##........######..########.####.########..########.......##.....#######...######....######...########.########.....######.....##.......##....########.####.##....##..######.......######.....##....##.....##.##.....##....##...
  */

  private observeMatSlideToggleCheckedClass() {
    const nativeElement = this.el.nativeElement;
    const mdcSwitch = nativeElement.querySelector('.mdc-switch');

    const applySlideToggleState = () => {
      if (
        nativeElement.classList.contains('mat-checked') ||
        nativeElement.classList.contains('mat-mdc-slide-toggle-checked') ||
        mdcSwitch?.classList.contains('mdc-switch--selected')
      ) {
        this.setMatSlideToggleStyling();
      }
      else {
        this.removeMatSlideToggleStyling();
      }
    };

    const observer = new MutationObserver(() => {
      applySlideToggleState();
    });

    applySlideToggleState();
    observer.observe(nativeElement, { attributes: true, attributeFilter: ['class'] });

    if (mdcSwitch) {
      observer.observe(mdcSwitch, { attributes: true, attributeFilter: ['class'] });
    }
  }

  setMatSlideToggleStyling() {
    const color = this.stylingColoursService.activeStylingColour[this.color];
    const nativeElement = this.el.nativeElement;
    const mdcSwitch = nativeElement.querySelector('.mdc-switch');
    const track = nativeElement.querySelector('.mdc-switch__track, .mat-slide-toggle-bar');
    const thumb = nativeElement.querySelector('.mdc-switch__thumb, .mat-slide-toggle-thumb');

    if (mdcSwitch) {
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-hover-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-pressed-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-focus-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-hover-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-pressed-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-focus-track-color', color, 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-track-opacity', '1', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-track-opacity', '1', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-hover-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-pressed-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-selected-focus-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-hover-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-pressed-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-focus-handle-color', '#fff', 'important');
      this.setTrackedStyle(mdcSwitch, 'opacity', '1', 'important');
    }

    this.setTrackedStyle(nativeElement, 'opacity', '1', 'important');

    if (thumb) {
      this.setTrackedRendererStyle(thumb, 'background-color', '#fff');
    }

    if (track) {
      this.setTrackedRendererStyle(track, 'background-color', color, RendererStyleFlags2.Important);
      this.setTrackedRendererStyle(track, 'opacity', '1', RendererStyleFlags2.Important);
    }
  }

  removeMatSlideToggleStyling() {
    const nativeElement = this.el.nativeElement;
    const mdcSwitch = nativeElement.querySelector('.mdc-switch');

    if (mdcSwitch) {
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-hover-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-pressed-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-focus-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-hover-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-pressed-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-focus-track-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-track-opacity');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-track-opacity');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-hover-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-pressed-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-selected-focus-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-hover-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-pressed-handle-color');
      this.removeTrackedStyle(mdcSwitch, '--mdc-switch-disabled-selected-focus-handle-color');
      this.removeTrackedStyle(mdcSwitch, 'opacity');
    }

    this.removeTrackedStyle(nativeElement, 'opacity');

    const circle = this.el.nativeElement.querySelector('.mat-slide-toggle-thumb, .mdc-switch__thumb');
    const bar = this.el.nativeElement.querySelector('.mat-slide-toggle-bar, .mdc-switch__track');

    if (circle) {
      this.setTrackedRendererStyle(circle, 'background-color', '');
    }

    if (bar) {
      this.setTrackedRendererStyle(bar, 'background-color', '');
      this.setTrackedRendererStyle(bar, 'opacity', '');
    }
  }

  /*
  .##.....##....###....########.....######..##.......####.########..########....########..#######...######....######...##.......########.....######..########.##....##.##.......####.##....##..######......########.##....##.########.
  .###...###...##.##......##.......##....##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##..........##....##....##.....##..##..##........##..###...##.##....##.....##.......###...##.##.....##
  .####.####..##...##.....##.......##.......##........##..##.....##.##.............##....##.....##.##........##........##.......##..........##..........##......####...##........##..####..##.##...........##.......####..##.##.....##
  .##.###.##.##.....##....##........######..##........##..##.....##.######.........##....##.....##.##...####.##...####.##.......######.......######.....##.......##....##........##..##.##.##.##...####....######...##.##.##.##.....##
  .##.....##.#########....##.............##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##................##....##.......##....##........##..##..####.##....##.....##.......##..####.##.....##
  .##.....##.##.....##....##.......##....##.##........##..##.....##.##.............##....##.....##.##....##..##....##..##.......##..........##....##....##.......##....##........##..##...###.##....##.....##.......##...###.##.....##
  .##.....##.##.....##....##........######..########.####.########..########.......##.....#######...######....######...########.########.....######.....##.......##....########.####.##....##..######......########.##....##.########.
  */

  /*
  .##.....##....###....########.....######..##.....##.########..######..##....##.########...#######..##.....##.....######..########.##....##.##.......####.##....##..######.......######..########....###....########..########
  .###...###...##.##......##.......##....##.##.....##.##.......##....##.##...##..##.....##.##.....##..##...##.....##....##....##.....##..##..##........##..###...##.##....##.....##....##....##......##.##...##.....##....##...
  .####.####..##...##.....##.......##.......##.....##.##.......##.......##..##...##.....##.##.....##...##.##......##..........##......####...##........##..####..##.##...........##..........##.....##...##..##.....##....##...
  .##.###.##.##.....##....##.......##.......#########.######...##.......#####....########..##.....##....###........######.....##.......##....##........##..##.##.##.##...####.....######.....##....##.....##.########.....##...
  .##.....##.#########....##.......##.......##.....##.##.......##.......##..##...##.....##.##.....##...##.##............##....##.......##....##........##..##..####.##....##...........##....##....#########.##...##......##...
  .##.....##.##.....##....##.......##....##.##.....##.##.......##....##.##...##..##.....##.##.....##..##...##.....##....##....##.......##....##........##..##...###.##....##.....##....##....##....##.....##.##....##.....##...
  .##.....##.##.....##....##........######..##.....##.########..######..##....##.########...#######..##.....##.....######.....##.......##....########.####.##....##..######.......######.....##....##.....##.##.....##....##...
  */

  private observeMatCheckboxClass() {
    const linkElement = this.el.nativeElement;

    // Using MutationObserver to observe changes in class attribute
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (
            target.classList.contains('mat-checkbox-checked') ||
            target.classList.contains('mdc-checkbox--selected') ||
            target.classList.contains('mat-mdc-checkbox') && target.classList.contains('mat-mdc-checkbox-checked')
          ) {
            this.setMatCheckboxStyling();
          }
          else {
            this.removeMatCheckboxStyling();
          }
        }
      }
    });

    // Start observing the class attribute
    observer.observe(linkElement, { attributes: true });
  }

  setMatCheckboxStyling() {
    const background = this.el.nativeElement.querySelector('.mat-checkbox-background, .mdc-checkbox__background');

    if (background) {
      this.setTrackedRendererStyle(background, 'background-color', this.stylingColoursService.activeStylingColour[this.color]);
      this.setTrackedRendererStyle(background, 'border-color', this.stylingColoursService.activeStylingColour[this.color]);
    }
  }

  removeMatCheckboxStyling() {
    const background = this.el.nativeElement.querySelector('.mat-checkbox-background, .mdc-checkbox__background');

    if (background) {
      this.setTrackedRendererStyle(background, 'background-color', '');
      this.setTrackedRendererStyle(background, 'border-color', '');
    }
  }

  /*
  .##.....##....###....########.....######..##.....##.########..######..##....##.########...#######..##.....##.....######..########.##....##.##.......####.##....##..######......########.##....##.########.
  .###...###...##.##......##.......##....##.##.....##.##.......##....##.##...##..##.....##.##.....##..##...##.....##....##....##.....##..##..##........##..###...##.##....##.....##.......###...##.##.....##
  .####.####..##...##.....##.......##.......##.....##.##.......##.......##..##...##.....##.##.....##...##.##......##..........##......####...##........##..####..##.##...........##.......####..##.##.....##
  .##.###.##.##.....##....##.......##.......#########.######...##.......#####....########..##.....##....###........######.....##.......##....##........##..##.##.##.##...####....######...##.##.##.##.....##
  .##.....##.#########....##.......##.......##.....##.##.......##.......##..##...##.....##.##.....##...##.##............##....##.......##....##........##..##..####.##....##.....##.......##..####.##.....##
  .##.....##.##.....##....##.......##....##.##.....##.##.......##....##.##...##..##.....##.##.....##..##...##.....##....##....##.......##....##........##..##...###.##....##.....##.......##...###.##.....##
  .##.....##.##.....##....##........######..##.....##.########..######..##....##.########...#######..##.....##.....######.....##.......##....########.####.##....##..######......########.##....##.########.
  */

  /*
  .......##.....##....###....########....##.......####..######..########.....#######..########..########.####..#######..##....##.....######..########.##....##.##.......####.##....##..######.......######..########....###....########..########
  .......###...###...##.##......##.......##........##..##....##....##.......##.....##.##.....##....##.....##..##.....##.###...##....##....##....##.....##..##..##........##..###...##.##....##.....##....##....##......##.##...##.....##....##...
  .......####.####..##...##.....##.......##........##..##..........##.......##.....##.##.....##....##.....##..##.....##.####..##....##..........##......####...##........##..####..##.##...........##..........##.....##...##..##.....##....##...
  .......##.###.##.##.....##....##.......##........##...######.....##.......##.....##.########.....##.....##..##.....##.##.##.##.....######.....##.......##....##........##..##.##.##.##...####.....######.....##....##.....##.########.....##...
  .......##.....##.#########....##.......##........##........##....##.......##.....##.##...........##.....##..##.....##.##..####..........##....##.......##....##........##..##..####.##....##...........##....##....#########.##...##......##...
  .......##.....##.##.....##....##.......##........##..##....##....##.......##.....##.##...........##.....##..##.....##.##...###....##....##....##.......##....##........##..##...###.##....##.....##....##....##....##.....##.##....##.....##...
  .......##.....##.##.....##....##.......########.####..######.....##........#######..##...........##....####..#######..##....##.....######.....##.......##....########.####.##....##..######.......######.....##....##.....##.##.....##....##...
  */

  private observeMatListOptionClass() {
    const linkElement = this.el.nativeElement;

    // Using MutationObserver to observe changes in class attribute
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'aria-selected') {
          const target = mutation.target as HTMLElement;
          if(target.getAttribute("aria-selected") === "true") {
            // Do something when the 'this.setColorSettings?.setIfClass' class is added
            this.setMatListOptionStyling();
            // Call your function or execute your code here
          }
          else {
            this.removeMatListOptionStyling();
          }
        }
      }
    });

    // Start observing the class attribute
    observer.observe(linkElement, { attributes: true });
  }

  setMatListOptionStyling() {
    const background = this.el.nativeElement.querySelector('.mat-pseudo-checkbox, .mdc-list-item__start');

    if (background) {
      this.setTrackedRendererStyle(background, 'background', this.stylingColoursService.activeStylingColour[this.color]);
      this.setTrackedRendererStyle(background, 'border-color', this.stylingColoursService.activeStylingColour[this.color]);
    }
  }

  removeMatListOptionStyling() {
    const background = this.el.nativeElement.querySelector('.mat-pseudo-checkbox, .mdc-list-item__start');

    if (background) {
      this.setTrackedRendererStyle(background, 'background', '');
      this.setTrackedRendererStyle(background, 'border-color', '');
    }
  }

  /*
  .##.....##....###....########....##.......####..######..########.....#######..########..########.####..#######..##....##.....######..########.##....##.##.......####.##....##..######......########.##....##.########.
  .###...###...##.##......##.......##........##..##....##....##.......##.....##.##.....##....##.....##..##.....##.###...##....##....##....##.....##..##..##........##..###...##.##....##.....##.......###...##.##.....##
  .####.####..##...##.....##.......##........##..##..........##.......##.....##.##.....##....##.....##..##.....##.####..##....##..........##......####...##........##..####..##.##...........##.......####..##.##.....##
  .##.###.##.##.....##....##.......##........##...######.....##.......##.....##.########.....##.....##..##.....##.##.##.##.....######.....##.......##....##........##..##.##.##.##...####....######...##.##.##.##.....##
  .##.....##.#########....##.......##........##........##....##.......##.....##.##...........##.....##..##.....##.##..####..........##....##.......##....##........##..##..####.##....##.....##.......##..####.##.....##
  .##.....##.##.....##....##.......##........##..##....##....##.......##.....##.##...........##.....##..##.....##.##...###....##....##....##.......##....##........##..##...###.##....##.....##.......##...###.##.....##
  .##.....##.##.....##....##.......########.####..######.....##........#######..##...........##....####..#######..##....##.....######.....##.......##....########.####.##....##..######......########.##....##.########.
  */

  /*
  .##.....##....###....########....########.....###....########..####..#######.....########..##.....##.########.########..#######..##....##.....######..########....###....########..########
  .###...###...##.##......##.......##.....##...##.##...##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.###...##....##....##....##......##.##...##.....##....##...
  .####.####..##...##.....##.......##.....##..##...##..##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.####..##....##..........##.....##...##..##.....##....##...
  .##.###.##.##.....##....##.......########..##.....##.##.....##..##..##.....##....########..##.....##....##.......##....##.....##.##.##.##.....######.....##....##.....##.########.....##...
  .##.....##.#########....##.......##...##...#########.##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.##..####..........##....##....#########.##...##......##...
  .##.....##.##.....##....##.......##....##..##.....##.##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.##...###....##....##....##....##.....##.##....##.....##...
  .##.....##.##.....##....##.......##.....##.##.....##.########..####..#######.....########...#######.....##.......##.....#######..##....##.....######.....##....##.....##.##.....##....##...
  */

  private observeMatRadioButtonClass() {
    const linkElement = this.el.nativeElement;

    // Using MutationObserver to observe changes in class attribute
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'class') {
          const target = mutation.target as HTMLElement;
          if (
            target.classList.contains('mat-radio-checked') ||
            target.classList.contains('mdc-radio--checked') ||
            target.classList.contains('mat-mdc-radio-button') && target.classList.contains('mat-mdc-radio-checked')
          ) {
            this.setMatRadioButtonStyling();
          }
          else {
            this.removeMatRadioButtonStyling();
          }
        }
      }
    });

    // Start observing the class attribute
    observer.observe(linkElement, { attributes: true });
  }

  setMatRadioButtonStyling() {
    const outerCircle = this.el.nativeElement.querySelector('.mat-radio-outer-circle, .mdc-radio__outer-circle');
    const innerCircle = this.el.nativeElement.querySelector('.mat-radio-inner-circle, .mdc-radio__inner-circle');

    if (outerCircle) {
      this.setTrackedRendererStyle(outerCircle, 'border-color', this.stylingColoursService.activeStylingColour[this.color]);
    }
    if (innerCircle) {
      this.setTrackedRendererStyle(innerCircle, 'background-color', this.stylingColoursService.activeStylingColour[this.color]);
    }
  }

  removeMatRadioButtonStyling() {
    const outerCircle = this.el.nativeElement.querySelector('.mat-radio-outer-circle, .mdc-radio__outer-circle');
    const innerCircle = this.el.nativeElement.querySelector('.mat-radio-inner-circle, .mdc-radio__inner-circle');

    if (outerCircle) {
      this.setTrackedRendererStyle(outerCircle, 'border-color', 'rgba(0, 0, 0, 0.54)');
    }
    if (innerCircle) {
      this.setTrackedRendererStyle(innerCircle, 'background-color', '');
    }
  }

  /*
  ....##.....##....###....########....########.....###....########..####..#######.....########..##.....##.########.########..#######..##....##....########.##....##.########.
  ....###...###...##.##......##.......##.....##...##.##...##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.###...##....##.......###...##.##.....##
  ....####.####..##...##.....##.......##.....##..##...##..##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.####..##....##.......####..##.##.....##
  ....##.###.##.##.....##....##.......########..##.....##.##.....##..##..##.....##....########..##.....##....##.......##....##.....##.##.##.##....######...##.##.##.##.....##
  ....##.....##.#########....##.......##...##...#########.##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.##..####....##.......##..####.##.....##
  ....##.....##.##.....##....##.......##....##..##.....##.##.....##..##..##.....##....##.....##.##.....##....##.......##....##.....##.##...###....##.......##...###.##.....##
  ....##.....##.##.....##....##.......##.....##.##.....##.########..####..#######.....########...#######.....##.......##.....#######..##....##....########.##....##.########.
  */

  /*
  .##....##.########.##....##.########...#######.....########.########..########.########.##.....##.####.########.##......##.....######..########....###....########..########
  .##...##..##.......###...##.##.....##.##.....##.......##....##.....##.##.......##.......##.....##..##..##.......##..##..##....##....##....##......##.##...##.....##....##...
  .##..##...##.......####..##.##.....##.##.....##.......##....##.....##.##.......##.......##.....##..##..##.......##..##..##....##..........##.....##...##..##.....##....##...
  .#####....######...##.##.##.##.....##.##.....##.......##....########..######...######...##.....##..##..######...##..##..##.....######.....##....##.....##.########.....##...
  .##..##...##.......##..####.##.....##.##.....##.......##....##...##...##.......##........##...##...##..##.......##..##..##..........##....##....#########.##...##......##...
  .##...##..##.......##...###.##.....##.##.....##.......##....##....##..##.......##.........##.##....##..##.......##..##..##....##....##....##....##.....##.##....##.....##...
  .##....##.########.##....##.########...#######........##....##.....##.########.########....###....####.########..###..###......######.....##....##.....##.##.....##....##...
  */

  private observeKendoTreeviewClass(colorRule?: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    const treeViewElement = this.el.nativeElement;

    // Using MutationObserver to observe changes in attributeName aria-checked
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.attributeName === 'aria-checked') {
          const target = mutation.target as HTMLElement;
          if(target.getAttribute("aria-checked") === "true") {
            this.setKendoTreeviewStyling(target, colorRule);
          }
          else {
            this.removeKendoTreeviewStyling(target);
          }
        }
      }
    });

    // Get all li elements
    const liElements = treeViewElement.querySelectorAll('li');

    // Loop through li elements and change checkbox color
    liElements.forEach(li => {
      const checkbox = li.querySelector('input');
      if (checkbox) {
        observer.observe(li, { attributes: true });
      }
    });
  }

  private setInitialKendoTreeviewStyling(colorRule?: {
    color?: string
    style?: string
    setIfClass?: string[]
    events?: string[]
    target?: string
  }) {
    const treeViewElement = this.el.nativeElement;

    // Get all li elements
    const liElements = treeViewElement.querySelectorAll('li');

    // Loop through li elements and change checkbox color
    liElements.forEach(li => {
      const checkbox = li.querySelector('input');
      if (checkbox && li.getAttribute('aria-checked')==='true') {
        this.setKendoTreeviewStyling(li, colorRule);
      }
    });
  }

  setKendoTreeviewStyling(li, colorRule) {
    const checkbox = li.querySelector('input');

    checkbox.setAttribute(
      'style',
      `
        background-color:${this.stylingColoursService.activeStylingColour[colorRule.color]} !important;
        border-color:${this.stylingColoursService.activeStylingColour[colorRule.color]} !important;
      `
    );
  }

  removeKendoTreeviewStyling(li) {
    const checkbox = li.querySelector('input');

    checkbox.setAttribute(
      'style',
      `
        background-color: '' !important;
        border-color: rgba(0, 0, 0, 0.08); !important;
      `
    );
  }

  /*
  .##....##.########.##....##.########...#######.....########.########..########.########.##.....##.####.########.##......##....########.##....##.########.
  .##...##..##.......###...##.##.....##.##.....##.......##....##.....##.##.......##.......##.....##..##..##.......##..##..##....##.......###...##.##.....##
  .##..##...##.......####..##.##.....##.##.....##.......##....##.....##.##.......##.......##.....##..##..##.......##..##..##....##.......####..##.##.....##
  .#####....######...##.##.##.##.....##.##.....##.......##....########..######...######...##.....##..##..######...##..##..##....######...##.##.##.##.....##
  .##..##...##.......##..####.##.....##.##.....##.......##....##...##...##.......##........##...##...##..##.......##..##..##....##.......##..####.##.....##
  .##...##..##.......##...###.##.....##.##.....##.......##....##....##..##.......##.........##.##....##..##.......##..##..##....##.......##...###.##.....##
  .##....##.########.##....##.########...#######........##....##.....##.########.########....###....####.########..###..###.....########.##....##.########.
  */


  private getColorTargets(styleProperty?: string, targetSelector?: string): HTMLElement[] {
    const nativeElement = this.el.nativeElement as HTMLElement;

    if (targetSelector) {
      const targets = Array.from(nativeElement.querySelectorAll(targetSelector)) as HTMLElement[];
      return targets.length ? targets : [nativeElement];
    }

    if (styleProperty === 'background' || styleProperty === 'background-color') {
      return [nativeElement];
    }

    const childSelectors = [
      'mat-icon',
      '.mat-icon',
      '.material-icons',
      '.material-icons-outlined',
      'span',
      '.mat-mdc-list-item-unscoped-content',
      '.mdc-list-item__content',
      '.mdc-list-item__primary-text',
      '.mat-list-item-content'
    ].join(', ');

    return [
      nativeElement,
      ...Array.from(nativeElement.querySelectorAll(childSelectors)) as HTMLElement[]
    ];
  }

  setColor(colorRule?: {
    color?: string;        // Color to set
    style?: string;        // Style property to apply the color to
    setIfClass?: string[]; // Classes to check before applying the color
    events?: string[];     // Events (not used in this function)
    target?: string;       // Target (not used in this function)
  }) {
    const nativeElement = this.el.nativeElement;

    if (!colorRule) return;

    const styleProperty = colorRule.style || 'color';

    if (colorRule.setIfClass && !this.haveSharedItems(nativeElement.classList, colorRule.setIfClass)) {
      return;
    }

    const colorName = colorRule.color || this.color;
    const colorValue = colorName ? this.stylingColoursService.activeStylingColour[colorName] : undefined;

    if (!colorValue) {
      return;
    }

    const targets = this.getColorTargets(styleProperty, colorRule.target);

    targets.forEach(target => {
      if (styleProperty && target) {
        this.setTrackedStyle(target, styleProperty, colorValue, 'important');
      }
    });

    this.originalStyle = nativeElement.getAttribute('style');
  }

  removeColor(colorRule?: {
    color?: string;
    style?: string;
    setIfClass?: string[];
    events?: string[];
    target?: string;
  }) {
    const nativeElement = this.el.nativeElement;

    if (colorRule?.setIfClass && !this.haveSharedItems(nativeElement.classList, colorRule.setIfClass)) {
      return;
    }

    this.removeTrackedStyle(nativeElement, colorRule?.style || 'color');
  }

  setColorRegardlessOfClass(colorRule?: {
    color?: string;
    style?: string;
    setIfClass?: string[];
    events?: string[];
    target?: string;
  }) {
    const colorValue = this.stylingColoursService.activeStylingColour[colorRule?.color || this.color];
    const targets = this.getColorTargets(colorRule?.style, colorRule?.target);

    targets.forEach(target => {
      if (colorRule?.style) {
        this.setTrackedStyle(target, colorRule.style, colorValue, 'important');
      }
      else {
        this.setTrackedStyle(target, 'color', colorValue, 'important');
      }
    });
  }

  removeColorRegardlessOfClass(colorRule?: {
    color?: string;
    style?: string;
    setIfClass?: string[];
    events?: string[];
    target?: string;
  }){
    const targets = this.getColorTargets(colorRule?.style, colorRule?.target);

    targets.forEach(target => {
      if (colorRule?.style) {
        this.removeTrackedStyle(target, colorRule.style);
      }
      else {
        this.removeTrackedStyle(target, 'color');
      }
    });
  }

  setMatStrokedButtonStyling() {
    const nativeElement = this.el.nativeElement;
    const color = this.stylingColoursService.activeStylingColour[this.color];

    this.setTrackedStyle(nativeElement, 'color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mdc-outlined-button-label-text-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mat-mdc-button-persistent-ripple-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mat-mdc-button-ripple-color', `color-mix(in srgb, ${color} 10%, transparent)`, 'important');
  }

  setMatRaisedButtonStyling() {
    const nativeElement = this.el.nativeElement;
    const color = this.stylingColoursService.activeStylingColour[this.color];

    this.setTrackedStyle(nativeElement, 'background', color, 'important');
    this.setTrackedStyle(nativeElement, 'background-color', color, 'important');
    this.setTrackedStyle(nativeElement, 'color', '#fff', 'important');
    this.setTrackedStyle(nativeElement, '--mdc-filled-button-container-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mdc-filled-button-label-text-color', '#fff', 'important');
    this.setTrackedStyle(nativeElement, '--mdc-outlined-button-container-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mdc-outlined-button-label-text-color', '#fff', 'important');
    this.setTrackedStyle(nativeElement, '--mdc-unelevated-button-container-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mdc-unelevated-button-label-text-color', '#fff', 'important');
  }

  setMatChipStyling() {
    const nativeElement = this.el.nativeElement;
    this.setTrackedStyle(nativeElement, 'background', this.stylingColoursService.activeStylingColour[this.color], 'important');
  }

  setMatProgressBarStyling() {
    const nativeElement = this.el.nativeElement;
    const color = this.stylingColoursService.activeStylingColour[this.color];

    const progressFill = nativeElement.querySelector('.mat-progress-bar-fill, .mdc-linear-progress__bar-inner');
    const buffer = nativeElement.querySelector('.mat-progress-bar-buffer, .mdc-linear-progress__buffer');

    if (progressFill) {
      this.setTrackedRendererStyle(progressFill, 'background-color', color);
      this.setTrackedRendererStyle(progressFill, 'border-color', color);
    }

    if (buffer) {
      this.setTrackedRendererStyle(buffer, 'background-color', '#e3e3e3');
    }

    this.setTrackedStyle(nativeElement, '--mdc-linear-progress-active-indicator-color', color, 'important');
    this.setTrackedStyle(nativeElement, '--mdc-linear-progress-track-color', '#e3e3e3', 'important');
  }

  setMatBadgeStyling(colorRule: {
    color?: string;
    style?: string;
    setIfClass?: string[];
    events?: string[];
    target?: string;
  }) {
    const badge = this.el.nativeElement.querySelector('.mat-badge-content');

    this.setTrackedRendererStyle(badge, 'background', `${this.stylingColoursService.activeStylingColour[colorRule.color]}`);
  }
}
